import { Avatar } from "@/components/shared/Avatar";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { formatFileSize } from "@/lib/format";
import { formatMessageTime, splitMentions, type ChannelMessage } from "@/lib/chat-room/messages";
import type { ReactNode } from "react";

interface ChatMessageItemProps {
  message: ChannelMessage;
  selfId: string | null;
  /** Navn på de nevnte, for å utheve @-nevninger i teksten. */
  mentionedUsers: { id: string; full_name: string }[];
  /** Reaksjoner og andre handlinger under meldingen. */
  footer?: ReactNode;
  /** Lenke-brikke under en systemmelding. */
  link?: ReactNode;
}

const isImage = (mime: string | null, name: string) =>
  (mime ? mime.startsWith("image/") : false) || /\.(png|jpe?g|gif|webp)$/i.test(name);

/** Én melding i Chat-rommet: avatar, navn, tidspunkt, tekst med @-nevninger og vedlegg. */
export function ChatMessageItem({ message, selfId, mentionedUsers, footer, link }: ChatMessageItemProps) {
  const isSystem = message.kind === "system";
  const parts = splitMentions(message.body, mentionedUsers);

  return (
    <article className="flex gap-3 py-2.5" aria-label={`Melding fra ${message.authorName}`}>
      {isSystem ? (
        <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
          <SrIcon name="fox" className="h-[18px] w-[18px]" />
        </span>
      ) : (
        <Avatar name={message.authorName} avatarUrl={message.authorAvatarUrl} className="h-9 w-9 shrink-0" />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold">{isSystem ? "Revehiet" : message.authorName}</span>
          <time dateTime={message.createdAt} className="text-xs text-muted-foreground">
            {formatMessageTime(message.createdAt)}
          </time>
        </div>
        {parts.length > 0 && (
          <p className={cn("whitespace-pre-wrap break-words text-[15px] leading-relaxed", isSystem && "text-muted-foreground")}>
            {parts.map((p, i) =>
              p.mentionUserId ? (
                <span
                  key={i}
                  className={cn(
                    "rounded px-1 py-px font-semibold",
                    p.mentionUserId === selfId
                      ? "bg-[var(--sr-reverosa)] text-[var(--sr-revemoerk)]"
                      : "bg-primary/10 text-primary",
                  )}
                >
                  {p.text}
                </span>
              ) : (
                <span key={i}>{p.text}</span>
              ),
            )}
          </p>
        )}
        {message.attachments.map((a) =>
          isImage(a.mimeType, a.name) ? (
            <a key={a.url} href={a.url} target="_blank" rel="noopener noreferrer" className="self-start">
              <img src={a.url} alt={a.name} className="max-h-[220px] max-w-[280px] rounded-lg border object-cover" />
            </a>
          ) : (
            <a
              key={a.url}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 self-start rounded-lg border-[1.5px] border-border bg-[hsl(var(--surface-light))] px-3 py-2 text-[13px] hover:bg-secondary"
            >
              <SrIcon name="file-text" className="h-4 w-4" />
              <span className="max-w-[240px] truncate font-medium">{a.name}</span>
              {a.sizeBytes != null && <span className="text-muted-foreground">{formatFileSize(a.sizeBytes)}</span>}
            </a>
          ),
        )}
        {link}
        {footer}
      </div>
    </article>
  );
}
